// store/data/global.js
import { createSlice } from "@reduxjs/toolkit";

export const navItems = [
  { path: "/", key: "/", name: { EN: "Home", JA: "ホーム" } },
  { path: "/industries", key: "industries", name: { EN: "Industries & Advantages", JA: "対象業界と強み" } },
  { path: "/services", key: "services", name: { EN: "Services", JA: "事業内容" } },
  { path: "/future", key: "future", name: { EN: "Future", JA: "未来" } },
];

const globalSlice = createSlice({
  name: 'global',
  initialState: {
    currentLanguage: 'JA',
    translations: {
      IndexBannerList: {
        JA: {
          headerONE: '限りない',
          headerTWO: 'コンピューティング力を解き放つ',
          subheader: [
            '最先端のコンピューティング能力を活用し、無限の可能性を引き出します。',
            'スケーラブルで効率的なソリューションを通じて、AIなどのイノベーションを推進します。',
          ],
        },
        EN: {
          headerONE: 'Unleashing Limitless ',
          headerTWO: 'Compute Potential',
          subheader: [
            'Harness state-of-the-art computing power to unleash limitless potential.',
            'Driving innovation in AI, and more through scalable, efficient solutions.'
          ]
        }
      },
      IndexHistoryList: {
        JA: {
          caption: '会社の沿革',
          content: [
            '2024年10月、東京で設立された当ブランドは、当初ネットワーク通信とデータセキュリティに注力しました。その後、AIコンピューティングサポート、算力リース、環境に配慮した持続可能な運用に事業を集中させました。',
            'AIコンピューティングサポート、算力リース、環境に配慮した持続可能な運用に事業を集中。技術を導きとし、現場の実際的な課題に一貫して取り組み、お客様に多対一の専任サービスを提供しています。',
            '人工知能、ビッグデータ、科学研究など、多様な業界を支える最先端インフラを備えています。',
          ],
        },
        EN: {
          caption: 'HISTORY',
          content: [
            'Founded in Tokyo in October 2024, this brand initially focused on network communications and data security. It then gradually concentrated on AI computing support, computing power leasing, and environmentally sustainable operations.',
            'It then gradually concentrated on AI computing support, computing power leasing, and environmentally sustainable operations. Guided by technology, it consistently addresses real-world on-site challenges and provides multi-to-one dedicated services to its clients. ',
            ' We are equipped with cutting-edge infrastructure to support diverse industries, including artificial intelligence, big data, and scientific research.'
          ]
        }
      },
      IndexCultureList: {
        JA: [
          {
            header: '企業理念',
            description: [
              '「テクノロジーで未来の生産モデルを創造する」',
              '私たちは、テクノロジーが単なる効率向上のツールではなく、働き方を変革し、社会の進歩を推進する根本的な力であると確信しています。',
              '生産現場の実際のニーズに耳を傾け、柔軟なオンデマンドAI算力リースを中核事業とし、カスタマイズされた高信頼性コンピューティングソリューションを提供します。お客様の計算ボトルネックの克服と技術導入障壁の低減を支援します。',
              '共により効率的で持続可能な未来の生産モデルの構築に取り組み、社会経済の高質な発展に具体的な貢献をします。',
            ],
          },
          {
            header: '企業文化の雰囲気',
            description: [
              '「ゼロベース思考」を提唱し、固定観念にとらわれないよう社員の挑戦を奨励します。',
              '「対等なコミュニケーション」の仕組みを推進し、オープンで包摂的な職場環境を創出します。',
              '社員のワークライフバランスを重視し、福利厚生とキャリア開発体制を充実させています。',
            ],
          },
        ],
        EN: [
          {
            header: 'Corporate Philosophy',
            description: [
              '"Creating the Future Production Model Through Technology"',
              'We firmly believe that technology is not merely a tool for improving efficiency, but a fundamental force that transforms the way we work and drives social progress. ',
              'By listening closely to the real needs of production sites, we provide flexible, on-demand AI computing power leasing as our core offering, along with customized, highly reliable computing solutions. We help our customers overcome computational bottlenecks and lower the barriers to technology adoption. ',
              'Together, we are committed to building more efficient and sustainable future production models, making tangible contributions to high-quality social and economic development.'
            ]
          },
          {
            header: 'Corporate Culture Atmosphere',
            description: [
              'We advocate "zero-based thinking" and encourage employees to break free from the constraints of fixed perceptions. ',
              'We promote an "equal communication" mechanism to create an open and inclusive working environment. ',
              'We value the work-life balance of employees and improve the welfare security and career development system.',
            ]
          }
        ]
      },
      IndexTeamList: {
        JA: {
          description: [
            '当社のチームは、コンピューターエンジニアリング、サステナビリティ、人工知能（AI）技術など、多様な専門知識を持つ情熱的なメンバーで構成されています。',
            '創造性、協力、卓越性へのこだわりを重視し、すべてのプロジェクトに反映しています。',
            '包摂的でダイナミックな職場環境を育み、チームがお客様に変革的なソリューションを提供できるよう支援しています。',
          ],
          list: [
            {
              header: '調達・サプライチェーンチーム',
              lineCover: require('@/assets/home/Team1.webp'),
            },
            {
              header: '営業・ビジネス開発チーム',
              lineCover: require('@/assets/home/Team2.webp'),
            },
            {
              header: 'マーケティングチーム',
              lineCover: require('@/assets/home/Team3.webp'),
            },
            {
              header: '技術・プロダクトマネジメントチーム',
              lineCover: require('@/assets/home/Team4.webp'),
            },
            {
              header: 'カスタマーサクセス・アフターセールスチーム',
              lineCover: require('@/assets/home/Team5.webp'),
            },
            {
              header: 'バックオフィスサポートチーム',
              lineCover: require('@/assets/home/Team6.webp'),
            },
          ],
        },
        EN: {
          description: [
            'Our team is composed of passionate individuals with diverse expertise, including computer engineering, sustainability, and artificial intelligence (AI) technologies.',
            'We value creativity, collaboration, and a commitment to excellence, which are reflected in every project we undertake.',
            'By fostering an inclusive and dynamic work environment, we empower our team to deliver transformative solutions for our clients.',
          ],
          list: [
            {
              header: 'Procurement & Supply Chain Team',
              lineCover: require('@/assets/home/Team1.webp'),
            },
            {
              header: 'Sales & Business Development Team',
              lineCover: require('@/assets/home/Team2.webp'),
            },
            {
              header: 'Marketing Team',
              lineCover: require('@/assets/home/Team3.webp'),
            },
            {
              header: 'Technical & Product Management Team',
              lineCover: require('@/assets/home/Team4.webp'),
            },
            {
              header: 'Customer Success & After-Sales Service Team',
              lineCover: require('@/assets/home/Team5.webp'),
            },
            {
              header: 'Back-Office Support Teams',
              lineCover: require('@/assets/home/Team6.webp'),
            },
          ],
        },
      },
      IndexDutyList: {
        JA: [
          {
            header: '当社の責任',
            description: [
              '「テクノロジーで社会課題を解決する」という使命のもと、AI技術、算力リースサービス、省エネで環境に配慮したソリューションを提供し、お客様の事業成長を支援しています。',
              '同時に、資源利用効率の継続的な向上、環境負荷の低減、地域コミュニティとの連携に積極的に取り組んでいます。',
              'これらの具体的な行動を通じて、持続可能な社会の構築に貢献する責任を果たしています。',
            ],
          },
        ],
        EN: [
          {
            header: 'Our responsibilities',
            description:[
              'Guided by our mission “Solving Social Issues with Technology,” we support our clients’ business growth by providing AI technology, computing power leasing services, and energy-saving, environmentally friendly solutions. ',
              'At the same time, we are committed to continuously improving resource utilization efficiency, reducing environmental impact, and actively participating in local community partnerships. ',
              'Through these tangible actions, we fulfill our responsibility to help build a sustainable society.'
            ]
          }
        ]
      },
      IndexMilestonesList: {
        EN: [
          'Since our inception, we have successfully delivered reliable computing solutions to support AI and research projects across multiple industries.',
          'As a Japan-based company, we leverage Japan’s strategic geographical advantages and stable infrastructure to actively expand data centers in Southeast Asia, while operating on environmentally friendly energy. This strategy aligns with our commitment to a sustainable future.',
          'We have built a growing team of professionals dedicated to driving innovation and efficiency.',
        ],
        JA: [
          '設立以来、当社は複数の業界にわたるAIおよび研究プロジェクトを支援する、信頼性の高いコンピューティングソリューションを提供してきました。',
          '日本を拠点とする企業として、日本の戦略的な地理的優位性と安定したインフラを活かし、環境に優しいエネルギーで運営しながら、東南アジアへのデータセンター展開を積極的に推進しています。この戦略は、持続可能な未来へのコミットメントと一致しています。',
          'イノベーションと効率化を推進することに専念するプロフェッショナルチームを着実に拡大してきました。',
        ],
      },
      SustainableList: {//Sustainable Development
        JA: [
          {
             header: 'AI・機械学習',
             content:'モデルの学習と展開に比類のない計算能力を提供し、AI研究開発のブレークスルーを加速します。',
             lineCover:require('@/assets/Sustainable/Industries-We-Serve1_compressed.webp'),
          },
          {
             header: 'メディアレンダリング',
             content:'堅牢な計算リソースでメディア制作を支援し、高速レンダリングとシームレスな創造性を実現します。',
             lineCover:require('@/assets/Sustainable/Industries-We-Serve2_compressed.webp'),
          },
          {
             header: '科学研究',
             content:'高性能コンピューティングにより、複雑なデータ分析、ゲノミクス、シミュレーションなどの研究を支援します。',
             lineCover:require('@/assets/Sustainable/Industries-We-Serve3_compressed.webp'),
          },
        ],
        EN: [
          {
             header: 'AI & Machine Learning',
             content:'Delivering unparalleled computational power for model training and deployment, accelerating breakthroughs in AI research and development.',
             lineCover:require('@/assets/Sustainable/Industries-We-Serve1_compressed.webp'),
          },
          {
            header: 'Media Rendering',
            content:'Supporting media production with robust computational resources, enabling faster rendering and seamless creativity.',
            lineCover:require('@/assets/Sustainable/Industries-We-Serve2_compressed.webp'),
         },
         {
          header: 'Scientific Research',
          content:'Empowering researchers with high-performance computing for complex data analysis, genomics, and simulations.',
          lineCover:require('@/assets/Sustainable/Industries-We-Serve3_compressed.webp'),
         },
        ]
      },
      CaseStudiesList: {//Case Studies
       JA: [
            {             
              header: '算力リース',
              clientName:'柔軟なオンデマンドGPU算力を提供',
              clientPain:'短期テスト、長期プロジェクト、さまざまな用途に適したCPU/GPU算力リースサービスを提供します。お客様は大規模な設備投資なしに高性能コンピューティングリソースへ迅速にアクセスでき、技術導入の障壁を大幅に下げることができます。',
              solution:'算力レンタルサービスは、AI開発、複雑なデータ分析、その他データ集約型タスクなど、お客様固有のビジネスニーズに合わせた多様なオプションを提供します。',
              solutionImg:require('@/assets/CaseStudies/Cover.gif'),
              CoverImg:require('@/assets/CaseStudies/solution-1.webp'),     
              Results:[
                {
                  caption:'使いやすさ',
                  description:'シンプルなセットアップと統合で、迅速に利用を開始できます。',
                  Img:require('@/assets/CaseStudies/Studies1.webp'),
                },
                {
                  caption:'パフォーマンスモニタリング',
                  description:'リアルタイムのデータと分析で利用状況を追跡し、コストを最適化します。',
                  Img:require('@/assets/CaseStudies/Studies2.webp'),
                },
                {
                  caption:'カスタム構成',
                  description:'ワークロードに応じて、GPUまたはASICベースのソリューションから選択可能です。',
                  Img:require('@/assets/CaseStudies/Studies3.webp'),
                },
                {
                  caption:'顧客中心のサポート',
                  description:'経験豊富な技術チームによる24時間365日のサポートを提供します。',
                  Img:require('@/assets/CaseStudies/Studies4.webp'),
                },
              ]
            },
            {             
              header: 'AIコンピューティングサポート',
              clientName:'高性能AIコンピュート＆アクセラレーションソリューション',
              clientPain:'AI開発および応用シナリオを対象に、深層学習、大規模モデル学習、推論向けの専用コンピューティング環境を提供します。最適化されたソフトウェアスタックとスケジューリングシステムにより、学習効率の向上、推論レイテンシの低減、AI導入の加速を支援します。',
              solution:'AIが産業を変革する中、先進モデルの学習、テスト、展開に必要な堅牢なコンピューティングインフラを提供します。',
              solutionImg:require('@/assets/CaseStudies/Cover.gif'),
              CoverImg:require('@/assets/CaseStudies/solution-2.webp'),  
              Results:[
                {
                  caption:'エネルギー効率',
                  description:'エコフレンドリーなインフラで、持続可能なAI開発を支援します。',
                  Img:require('@/assets/CaseStudies/Studies1.webp'),
                },
                // {
                //   caption:'AIイノベーションの加速',
                //   description:'最先端技術でAIイノベーションを加速します。',
                //   Img:require('@/assets/CaseStudies/Studies2.webp'),
                // },
                {
                  caption:'専任AIエキスパート',
                  description:'AIパイプライン向けの構成最適化に関するコンサルテーションを提供します。',
                  Img:require('@/assets/CaseStudies/Studies2.webp'),
                },
                {
                  caption:'クラウドベースのスケーラビリティ',
                  description:'大規模な初期投資なしに、いつでもどこでもリソースにアクセスできます。',
                  Img:require('@/assets/CaseStudies/Studies3.webp'),
                },
                {
                  caption:'高性能GPU',
                  description:'深層学習やNLPなど、要求の高いAIワークロード向けに設計されています。',
                  Img:require('@/assets/CaseStudies/Studies4.webp'),
                },
              ]
            }, 
            {             
              header: '省エネ運用',
              clientName:'持続可能で高効率なコンピューティングによるグリーンIT推進',
              clientPain:'効率的な冷却技術、インテリジェントなエネルギー管理システム、省エネアーキテクチャの採用により、コンピューティングインフラのエネルギー消費削減に取り組み、PUEの継続的な最適化を行います。',
              solution:'イノベーションと環境責任のバランスを取るため、エコフレンドリーな運用を最優先しています。',
              solutionImg:require('@/assets/CaseStudies/Cover.gif'),
              CoverImg:require('@/assets/CaseStudies/solution-3.webp'),  
              Results:[
                {
                  caption:'クリーンエネルギー',
                  description:'太陽光発電、水力発電など、環境に優しいエネルギー源を活用します。',
                  Img:require('@/assets/CaseStudies/Studies1.webp'),
                },
                {
                  caption:'革新的冷却システム',
                  description:'自然冷却および先進的な冷却技術により、エネルギーコストを削減します。',
                  Img:require('@/assets/CaseStudies/Studies2.webp'),
                },
                {
                  caption:'政策への準拠',
                  description:'地域およびグローバルの環境基準に完全準拠しています。',
                  Img:require('@/assets/CaseStudies/Studies3.webp'),
                },
              ]
            },                       
        ],
        EN: [
            {             
              header: 'Computing Power Leasing',
              clientName:'We offer flexible, on-demand GPU',
              clientPain:'CPU computing power leasing services suitable for short-term testing, long-term projects, and various use cases. Clients can quickly access high-performance computing resources without heavy capital investment, significantly lowering the barrier to technology adoption.',
              solution:'Our computing power rental services offer versatile options tailored to your unique business needs, whether you’re working on AI development, complex data analytics, or other data-intensive tasks.',
              solutionImg:require('@/assets/CaseStudies/Cover.gif'),
              CoverImg:require('@/assets/CaseStudies/solution-1.webp'),  
              Results:[
                {
                  caption:'Ease of Use',
                  description:'Simple setup and integration to get started quickly.',
                  Img:require('@/assets/CaseStudies/Studies1.webp'),
                },
                {
                  caption:'Performance Monitoring',
                  description:'Real-time data and analytics to track usage and optimize costs.',
                  Img:require('@/assets/CaseStudies/Studies2.webp'),
                },
                {
                  caption:'Custom Configurations',
                  description:'Select from GPU or ASIC-based solutions depending on your workload.',
                  Img:require('@/assets/CaseStudies/Studies3.webp'),
                },
                {
                  caption:'Customer-Centric Support',
                  description:'24/7 assistance from experienced technical teams.',
                  Img:require('@/assets/CaseStudies/Studies4.webp'),
                },
              ]
            },
            {             
              header: 'AI Computing Support',
              clientName:'High-Performance AI Compute & Acceleration Solutions',
              clientPain:'Targeting AI development and application scenarios, we provide dedicated computing environments for deep learning, large-scale model training, and inference. With an optimized software stack and scheduling system, we help clients improve training efficiency, reduce inference latency, and accelerate AI deployment.',
              solution:'As AI reshapes industries, we provide the robust computing infrastructure needed to train, test, and deploy advanced models.',
              solutionImg:require('@/assets/CaseStudies/Cover.gif'),
              CoverImg:require('@/assets/CaseStudies/solution-2.webp'),  
              Results:[
                {
                  caption:'Energy Efficiency',
                  description:'Support sustainable AI development with eco-friendly infrastructure.',
                  Img:require('@/assets/CaseStudies/Studies1.webp'),
                },
                {
                  caption:'Accelerating AI Innovation',
                  description:'Accelerating AI Innovation with Cutting-Edge',
                  Img:require('@/assets/CaseStudies/Studies2.webp'),
                },
                {
                  caption:'Dedicated AI Experts',
                  description:'Consultation to optimize configurations for AI pipelines.',
                  Img:require('@/assets/CaseStudies/Studies3.webp'),
                },
                {
                  caption:'Cloud-Based Scalability',
                  description:'Access resources anywhere, anytime, without large upfront investments.',
                  Img:require('@/assets/CaseStudies/Studies4.webp'),
                },
                {
                  caption:'High-Performance GPUs',
                  description:'Designed for demanding AI workloads such as deep learning and NLP.',
                  Img:require('@/assets/CaseStudies/Studies4.webp'),
                },
              ]
            },          
            {             
              header: 'Energy-Efficient Operations',
              clientName:'Empowering Green IT with Sustainable, High-Efficiency Computing',
              clientPain:'We are committed to reducing energy consumption in computing infrastructure by adopting efficient cooling technologies, intelligent energy management systems, and energy-saving architectures to continuously optimize PUE. ',
              solution:'We prioritize eco-friendly operations to balance innovation and environmental responsibility.',
              solutionImg:require('@/assets/CaseStudies/Cover.gif'),
              CoverImg:require('@/assets/CaseStudies/solution-3.webp'),  
              Results:[
                {
                  caption:'Clean Energy',
                  description:'Utilizing solar PV, hydroelectric, and other environmentally friendly energy sources.',
                  Img:require('@/assets/CaseStudies/Studies1.webp'),
                },
                {
                  caption:'Innovative Cooling Systems',
                  description:'Reducing energy costs with natural and advanced cooling techniques.',
                  Img:require('@/assets/CaseStudies/Studies2.webp'),
                },
                {
                  caption:'Policy Alignment',
                  description:'Fully compliant with regional and global environmental standards.',
                  Img:require('@/assets/CaseStudies/Studies3.webp'),
                },
              ]
            },        
 
        ]
      },
      WHYChooseUsCustomized:{
        EN:[
          { 
             header: 'High Scalability', 
             Img:require('@/assets/Sustainable/Why-Choose-Us1_compressed.webp'), 
             content:'Dynamic solutions tailored to your needs, from startups to enterprises.',
          },
          { 
            header: 'Cutting-Edge Infrastructure', 
            Img:require('@/assets/Sustainable/Why-Choose-Us2_compressed.webp'), 
            content:'State-of-the-art GPU and ASIC platforms for maximum performance and reliability.',
         },
         { 
          header: 'Green Energy Commitment', 
          Img:require('@/assets/Sustainable/Why-Choose-Us3_compressed.webp'), 
          content:'Over 95% of our energy is sourced from environmentally friendly energy, reducing operational costs while prioritizing sustainability.',
         }, 
         { 
          header: '24/7 Support', 
          Img:require('@/assets/Sustainable/Why-Choose-Us4_compressed.webp'), 
          content:'Dedicated teams available round-the-clock to ensure uninterrupted service and customer satisfaction.',
         },
        ],
        JA:[
          { 
             header: '高いスケーラビリティ', 
             Img:require('@/assets/Sustainable/Why-Choose-Us1_compressed.webp'), 
             content:'スタートアップから大企業まで、お客様のニーズに合わせた動的なソリューションを提供します。',
          },
          { 
             header: '最先端インフラ', 
             Img:require('@/assets/Sustainable/Why-Choose-Us2_compressed.webp'), 
             content:'最高のパフォーマンスと信頼性を実現する、最先端のGPUおよびASICプラットフォーム。',
          },
          { 
             header: 'グリーンエネルギーへの取り組み', 
             Img:require('@/assets/Sustainable/Why-Choose-Us3_compressed.webp'), 
             content:'エネルギーの95%以上を環境に優しいエネルギー源から供給し、運用コストを削減しながら持続可能性を最優先します。',
          },
          { 
             header: '24時間365日サポート', 
             Img:require('@/assets/Sustainable/Why-Choose-Us4_compressed.webp'), 
             content:'専任チームが24時間365日体制でサービスをサポートし、中断のないサービスと顧客満足を保証します。',
          },         
        ],
      },
      FutureCooperation:{
        EN:[
          { 
             title: 'International Cooperation', 
             Img:require('@/assets/Future/Cooperation1.webp'), 
             content:[
              'Embracing the principle of open collaboration and mutual benefit, we actively engage in the global technology cooperation ecosystem. Partnering with leading technology companies and research institutions in Japan, Asia, Europe, and the United States, we jointly tackle key challenges in AI computing power leasing, efficient computing architectures, and energy-saving operations.',
              'By sharing cutting-edge technologies and innovation outcomes, participating in industry standard-setting, and leveraging collective resources, we accelerate the development and deployment of new computing services, enhance our global presence, and drive technological innovation in computing.',
             ],
          },
          { 
             title: 'Empowering Local Enterprises', 
             Img:require('@/assets/Future/Cooperation2.webp'), 
             content:[
              'By deeply understanding the needs of local enterprises and offering a rich portfolio of customized services, we provide solid support for the intelligent transformation of traditional manufacturing industries and the digital growth of emerging businesses. ',
              'With flexible AI computing power leasing, we lower the barriers to technology adoption; with energy-efficient computing operations, we optimize enterprise resource efficiency. This helps local enterprises enhance efficiency and competitiveness in global markets, drives the upgrade of Japan’s industrial structure, and strengthens the foundation of domestic industries.'
            ],
          },
          { 
            title: 'Compared with Industry Giants', 
            Img:require('@/assets/Future/Cooperation3.webp'), 
            content:[
             'We continue to increase R&D investment, strive to break through technological bottlenecks, and benchmark against industry leaders in key technical indicators. By improving service quality and expanding our business footprint, we enhance our brand’s international recognition.',
             'In cutting-edge fields such as AI computing power leasing and energy-efficient computing operations, as well as the integration of AI and industry, we align with industry giants on the global stage. Together, we lead new directions in industry development and build a mutually beneficial new landscape for the global technology industry.'
            ],
         },
        ],
        JA:[
          { 
             title: '国際連携', 
             Img:require('@/assets/Future/Cooperation1.webp'), 
             content:[
              'オープンな協力と相互利益の原則を掲げ、グローバルなテクノロジー協力エコシステムに積極的に参画します。日本、アジア、欧米の主要テクノロジー企業や研究機関と連携し、AI算力リース、高効率なコンピューティングアーキテクチャ、省エネ運用といった重要課題に共同で取り組みます。',
              '最先端技術と革新的成果を共有し、業界標準の策定に参画し、集合的なリソースを活用することで、新たなコンピューティングサービスの開発・展開を加速し、グローバルなプレゼンスを高め、コンピューティング分野の技術革新を推進します。',
             ],
          },
          { 
             title: '国内企業支援', 
             Img:require('@/assets/Future/Cooperation2.webp'), 
             content:[
              '国内企業のニーズを深く理解し、豊富なカスタマイズサービスを提供することで、伝統的製造業のスマート化変革および新興企業のデジタル成長に確かな支援を提供します。',
              '柔軟なAI算力リースにより技術導入の障壁を下げ、省エネ型のコンピューティング運用により企業のリソース効率を最適化します。これにより国内企業の効率とグローバル市場での競争力を高め、日本の産業構造のアップグレードを推進し、国内産業の基盤を強固にします。',
            ],
          },
          { 
             title: '業界リーダーと並走', 
             Img:require('@/assets/Future/Cooperation3.webp'), 
             content:[
             '研究開発への投資を継続的に強化し、技術的なボトルネックの突破に全力で取り組み、重要な技術指標において業界リーダーと対標します。サービス品質の向上と事業領域の拡大を通じて、ブランドの国際的認知度を高めます。',
             'AI算力リース、省エネ型コンピューティング運用、AIと産業の融合といった先端分野において、グローバルな舞台で業界大手企業と並走します。共に業界発展の新たな方向性をリードし、グローバルテクノロジー産業における相互利益の新しい構図を構築します。',
            ],
          },                    
        ],
      },
    }
  },
  reducers: {
    setLanguage: (state, action) => {
      state.currentLanguage = action.payload;
    }
  }
});

// 导出actions
export const { setLanguage } = globalSlice.actions;

// 导出选择器函数
export const getCurrentLanguage = (state) => state.global.currentLanguage;
export const getTranslations = (state) => state.global.translations;

export default globalSlice.reducer;